import React, { useEffect, useState } from 'react';
import {
  Package, Lock, Loader2, Plus, Search, Download, CheckCircle2, Truck,
  Award, DollarSign, User, MapPin, RefreshCw, KeyRound, Share2, Copy, Trash2,
  ChevronDown, ChevronUp, QrCode, MessageCircle,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import ShipmentQrScanner from '../components/ShipmentQrScanner';
import { useOfflineScans } from '../hooks/useOfflineScans';
import { SyncBadge } from '../components/SyncBadge';
import { Wifi, WifiOff, CloudUpload } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_LABELS = {
  pending: 'En attente', picked_up: 'Ramassé', in_transit: 'En transit',
  arrived_at_hub: 'Arrivé au dépôt', awaiting_pickup: 'En attente d\u2019être récupéré',
  out_for_delivery: 'En livraison',
  delivered: 'Livré', returned: 'Retourné', cancelled: 'Annulé',
};
const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  picked_up: 'bg-sky-100 text-sky-800 border-sky-200',
  in_transit: 'bg-blue-100 text-blue-800 border-blue-200',
  arrived_at_hub: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  awaiting_pickup: 'bg-[#F5EFE1] text-[#8F4426] border-[#B85C38]/40',
  out_for_delivery: 'bg-purple-100 text-purple-800 border-purple-200',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  returned: 'bg-rose-100 text-rose-800 border-rose-200',
  cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
};
const PAYMENT_STATUS_LABELS = { pending: 'À encaisser', paid: 'Payé', partial: 'Partiel', refunded: 'Remboursé' };
const TIER_COLORS = { none: 'bg-gray-100 text-gray-700', bronze: 'bg-orange-100 text-orange-800', silver: 'bg-gray-200 text-gray-800', gold: 'bg-amber-100 text-amber-800' };
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');

const PinPad = ({ value, onChange }) => (
  <div className="flex justify-center gap-2 my-6">
    {[0, 1, 2, 3].map(i => (
      <input
        key={i}
        type="tel" maxLength="1"
        className="w-12 h-14 text-3xl font-bold text-center border-2 border-amber-300 rounded-lg focus:border-amber-600 focus:outline-none"
        value={value[i] || ''}
        onChange={(e) => {
          const digit = e.target.value.replace(/\D/g, '').slice(-1);
          const next = value.split(''); next[i] = digit;
          onChange(next.join('').slice(0, 4));
          if (digit && i < 3) e.target.parentElement.children[i + 1]?.focus();
        }}
        data-testid={`pin-digit-${i}`}
      />
    ))}
  </div>
);

const Shipments = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem('shp_pin_ok'));
  const [pinConfigured, setPinConfigured] = useState(true);
  const [pin, setPin] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [showSetPin, setShowSetPin] = useState(false);
  const [newPin, setNewPin] = useState('');

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loyalty, setLoyalty] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showCreate, setShowCreate] = useState(false);
  const emptyPackage = { description: '', weight_kg: '', quantity: 1 };
  const emptyForm = {
    shipment_type: 'local', sender_name: '', sender_phone: '', recipient_name: '', recipient_phone: '',
    origin_city: 'Bamako', destination_city: '',
    packages: [{ ...emptyPackage }],
    amount: '', currency: 'XOF', payment_mode: 'prepaid', payment_status: 'pending', notes: '',
    estimated_departure_date: '', estimated_arrival_date: '',
    delivery_included: false,
  };
  const [form, setForm] = useState(emptyForm);
  const [senderLoyalty, setSenderLoyalty] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deliveringId, setDeliveringId] = useState(null);
  const [deliveryCode, setDeliveryCode] = useState('');
  const [expandedPkgs, setExpandedPkgs] = useState({}); // { [shipmentId]: true }
  const [pkgDelivery, setPkgDelivery] = useState(null); // { shipment, index }
  const [pkgDeliveryCode, setPkgDeliveryCode] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [etaEdit, setEtaEdit] = useState(null); // { shipment_id, tracking, departure, arrival }
  const offlineScans = useOfflineScans();

  const saveEtaDates = async () => {
    if (!etaEdit) return;
    try {
      await api.put(`/shipments/${etaEdit.shipment_id}`, {
        estimated_departure_date: etaEdit.departure || null,
        estimated_arrival_date: etaEdit.arrival || null,
      });
      toast.success('Dates prévisionnelles mises à jour');
      setEtaEdit(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/shipments/access-status');
        setPinConfigured(!!r.data.pin_configured);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [l, s, lo] = await Promise.all([
        api.get('/shipments', { params: { q: search || undefined, status: statusFilter === 'all' ? undefined : statusFilter } }),
        api.get('/shipments/stats'),
        api.get('/shipments/loyalty'),
      ]);
      setItems(l.data || []);
      setStats(s.data);
      setLoyalty(lo.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (unlocked) load(); }, [unlocked, statusFilter]);

  const verifyPin = async () => {
    setVerifying(true);
    try {
      await api.post('/shipments/verify-pin', { pin });
      sessionStorage.setItem('shp_pin_ok', '1');
      setUnlocked(true);
      toast.success('Module déverrouillé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PIN incorrect');
      setPin('');
    } finally { setVerifying(false); }
  };

  const setModulePin = async () => {
    if (!/^\d{4}$/.test(newPin)) return toast.error('Le PIN doit contenir 4 chiffres');
    try {
      await api.post('/shipments/set-pin', { pin: newPin });
      toast.success("PIN d'accès mis à jour. Communiquez-le aux agents autorisés.");
      setShowSetPin(false); setNewPin(''); setPinConfigured(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const createShipment = async () => {
    const validPkgs = (form.packages || []).filter(p => p.description && p.description.trim());
    if (!form.sender_name || !form.recipient_name || !form.destination_city || !form.amount) {
      return toast.error('Champs obligatoires manquants');
    }
    if (validPkgs.length === 0) {
      return toast.error('Ajoutez au moins un colis avec description');
    }
    setSaving(true);
    try {
      const packages = validPkgs.map(p => ({
        description: p.description.trim(),
        weight_kg: p.weight_kg ? parseFloat(p.weight_kg) : null,
        quantity: parseInt(p.quantity) || 1,
      }));
      const totalWeight = packages.reduce((sum, p) => sum + (p.weight_kg || 0) * (p.quantity || 1), 0);
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        weight_kg: totalWeight || null,
        description: packages.map(p => `${p.quantity}× ${p.description}`).join(' · '),
        packages,
        estimated_departure_date: form.estimated_departure_date || null,
        estimated_arrival_date: form.estimated_arrival_date || null,
      };
      await api.post('/shipments', payload);
      toast.success(`Envoi créé (${packages.length} colis)`);
      setShowCreate(false);
      setForm(emptyForm);
      setSenderLoyalty(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const duplicateShipment = (s) => {
    setForm({
      ...emptyForm,
      shipment_type: s.shipment_type || 'local',
      sender_name: s.sender_name || '',
      sender_phone: s.sender_phone || '',
      recipient_name: s.recipient_name || '',
      recipient_phone: s.recipient_phone || '',
      origin_city: s.origin_city || 'Bamako',
      destination_city: s.destination_city || '',
      payment_mode: s.payment_mode || 'prepaid',
      packages: [{ ...emptyPackage }],
    });
    setSenderLoyalty(null);
    // Charger la fidélité de l'expéditeur si téléphone connu
    if (s.sender_phone) fetchLoyalty(s.sender_phone);
    setShowCreate(true);
    toast.info(`Envoi dupliqué : ${s.sender_name} → ${s.recipient_name}. Ajoutez le(s) colis.`);
  };

  const fetchLoyalty = async (phone) => {
    if (!phone || phone.replace(/\D/g, '').length < 6) { setSenderLoyalty(null); return; }
    try {
      const r = await api.get(`/shipments/loyalty/${encodeURIComponent(phone)}`);
      setSenderLoyalty(r.data);
    } catch (e) { setSenderLoyalty(null); }
  };

  const addPackage = () => setForm(f => ({ ...f, packages: [...(f.packages || []), { ...emptyPackage }] }));
  const removePackage = (idx) => setForm(f => ({
    ...f,
    packages: f.packages.length > 1 ? f.packages.filter((_, i) => i !== idx) : f.packages,
  }));
  const updatePackage = (idx, key, val) => setForm(f => ({
    ...f,
    packages: f.packages.map((p, i) => i === idx ? { ...p, [key]: val } : p),
  }));

  const updateStatus = async (s, newStatus) => {
    if (!offlineScans.online) {
      offlineScans.enqueueStatus({
        shipment_id: s.id,
        tracking_number: s.tracking_number,
        status: newStatus,
      });
      // Mise à jour optimiste locale pour un feedback immédiat
      setItems(prev => prev.map(x => x.id === s.id ? { ...x, status: newStatus } : x));
      return;
    }
    try {
      await api.put(`/shipments/${s.id}`, { status: newStatus });
      toast.success('Statut mis à jour');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const updatePackageStatus = async (s, index, newStatus) => {
    if (!offlineScans.online) {
      offlineScans.enqueueStatus({
        shipment_id: s.id,
        tracking_number: s.tracking_number,
        package_index: index,
        status: newStatus,
      });
      // Mise à jour optimiste
      setItems(prev => prev.map(x => {
        if (x.id !== s.id) return x;
        const pkgs = [...(x.packages || [])];
        if (pkgs[index]) pkgs[index] = { ...pkgs[index], status: newStatus };
        return { ...x, packages: pkgs };
      }));
      return;
    }
    try {
      const r = await api.put(`/shipments/${s.id}/packages/${index}/status`, { status: newStatus });
      const globalChanged = r.data && r.data.status !== s.status;
      toast.success(globalChanged
        ? `Colis #${index + 1} → ${STATUS_LABELS[newStatus]} · statut global : ${STATUS_LABELS[r.data.status]}`
        : `Colis #${index + 1} → ${STATUS_LABELS[newStatus]}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const markPaid = async (s) => {
    try {
      await api.put(`/shipments/${s.id}`, { payment_status: 'paid' });
      toast.success('Paiement enregistré · fidélité mise à jour');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const confirmDelivery = async () => {
    if (!/^\d{4}$/.test(deliveryCode)) return toast.error('Code 4 chiffres attendu');
    try {
      await api.post(`/shipments/${deliveringId}/confirm-delivery`, { delivery_code: deliveryCode });
      toast.success('Livraison confirmée');
      setDeliveringId(null); setDeliveryCode('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const confirmPackageDelivery = async () => {
    if (!/^\d{4}$/.test(pkgDeliveryCode)) return toast.error('Code 4 chiffres attendu');
    if (!pkgDelivery) return;
    try {
      const r = await api.post(`/shipments/${pkgDelivery.shipment.id}/packages/${pkgDelivery.index}/confirm-delivery`, { delivery_code: pkgDeliveryCode });
      const nowAllDelivered = r.data.status === 'delivered';
      toast.success(nowAllDelivered
        ? `Colis #${pkgDelivery.index + 1} remis · TOUS les colis livrés`
        : `Colis #${pkgDelivery.index + 1} remis avec succès`);
      setPkgDelivery(null); setPkgDeliveryCode('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // ===== Scanner QR bordereau =====
  const handleScannedTracking = async (tn) => {
    // Cherche dans la liste locale puis focalise / ouvre livraison
    const found = items.find(x => (x.tracking_number || '').toUpperCase() === (tn || '').toUpperCase());
    if (found) {
      setScannerOpen(false);
      setSearch(tn);
      // Déplie automatiquement la carte pour montrer les colis
      setExpandedPkgs(prev => ({ ...prev, [found.id]: true }));
      toast.success(`Envoi ${tn} trouvé — ${found.recipient_name}`);
      // Scroll vers la carte après le rendu
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="toggle-packages-${found.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    } else {
      toast.warning(`Envoi ${tn} non trouvé dans cette agence. Recherche…`);
      setSearch(tn);
    }
  };

  const handleScanDeliver = (tn) => {
    const found = items.find(x => (x.tracking_number || '').toUpperCase() === (tn || '').toUpperCase());
    setScannerOpen(false);
    // Offline : demande le code puis met en queue
    if (!offlineScans.online) {
      const code = window.prompt(`Hors-ligne — Envoi ${tn}\n\nSaisissez le code 4 chiffres du destinataire :`);
      if (code && /^\d{4}$/.test(code.trim())) {
        offlineScans.enqueue({
          tracking_number: tn,
          delivery_code: code.trim(),
          // Si multi-colis, on cible le premier non-livré connu localement
          package_index: found && (found.packages || []).length > 0
            ? (found.packages.findIndex(p => (p.status || 'pending') !== 'delivered') >= 0
                ? found.packages.findIndex(p => (p.status || 'pending') !== 'delivered')
                : undefined)
            : undefined,
        });
      } else if (code) {
        toast.error('Code 4 chiffres attendu');
      }
      return;
    }
    if (!found) {
      toast.error(`Envoi ${tn} introuvable dans cette agence`);
      return;
    }
    if (found.status === 'delivered') {
      toast.info(`Envoi déjà livré`);
      return;
    }
    // Si multi-colis, on ouvre la remise du premier colis non-livré
    const pkgs = found.packages || [];
    if (pkgs.length > 0) {
      const idx = pkgs.findIndex(p => (p.status || 'pending') !== 'delivered');
      if (idx >= 0) {
        setPkgDelivery({ shipment: found, index: idx });
        setPkgDeliveryCode('');
        return;
      }
    }
    // Sinon livraison globale
    setDeliveringId(found.id);
    setDeliveryCode('');
  };

  const doPdf = async (s, endpoint, label) => {
    try {
      const r = await api.get(`/shipments/${s.id}/${endpoint}`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${label}_${s.tracking_number}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur PDF'); }
  };

  const relock = () => { sessionStorage.removeItem('shp_pin_ok'); setUnlocked(false); setPin(''); };

  const shareTracking = async (s) => {
    const url = `${window.location.origin}/track/${s.tracking_number}`;
    const msg = `Bonjour ${s.recipient_name}, votre colis (N° ${s.tracking_number}) est en route ! Suivez-le en direct ici : ${url}\nMerci pour votre confiance.`;
    // 1) Copy to clipboard
    try { await navigator.clipboard.writeText(msg); toast.success('Message + lien copiés dans le presse-papier'); }
    catch { /* fallback below */ }
    // 2) Open WhatsApp if a phone is available
    const phone = (s.recipient_phone || '').replace(/[^0-9+]/g, '').replace(/^\+/, '');
    if (phone) {
      const wa = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(wa, '_blank', 'noopener');
    }
  };

  const notifyArrivalAtHub = async (s) => {
    const url = `${window.location.origin}/track/${s.tracking_number}`;
    // Construire le récap des codes PIN par colis (le destinataire les présentera au retrait)
    const pkgs = Array.isArray(s.packages) ? s.packages : [];
    const pinLines = pkgs
      .map((p, i) => (p.pin_code ? `  Colis #${i + 1}${p.description ? ' (' + p.description + ')' : ''} → PIN : ${p.pin_code}` : null))
      .filter(Boolean)
      .join('\n');
    const pinBlock = pinLines ? `\n\nCode(s) de retrait :\n${pinLines}` : '';
    const msg =
      `Bonjour ${s.recipient_name},\n` +
      `Votre colis (N° ${s.tracking_number}) est ARRIVÉ AU DÉPÔT et est en attente d'être récupéré.\n` +
      `Merci de venir le retirer muni(e) d'une pièce d'identité.${pinBlock}\n\n` +
      `Suivi en direct : ${url}\n\n` +
      `Cordialement,\nL'équipe GAB'S SERVICES`;
    try { await navigator.clipboard.writeText(msg); toast.success('Message copié dans le presse-papier'); }
    catch { /* fallback below */ }
    const phone = (s.recipient_phone || '').replace(/[^0-9+]/g, '').replace(/^\+/, '');
    if (phone) {
      const wa = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(wa, '_blank', 'noopener');
    } else {
      toast.info('Aucun téléphone destinataire — message copié uniquement');
    }
  };

  // ---------- PIN gate ----------
  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto py-16" data-testid="shipments-locked">
        <Card className="border-2 border-amber-300">
          <CardContent className="p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-3">
              <Lock className="w-8 h-8 text-amber-700" />
            </div>
            <h1 className="text-2xl font-bold text-[#1B3A4B]">Module Colis</h1>
            <p className="text-gray-500 text-sm mt-2">
              {pinConfigured ? 'Saisissez le code 4 chiffres fourni par le super-administrateur.' : 'Le super-administrateur doit configurer un code d\u2019accès.'}
            </p>
            {pinConfigured && (
              <>
                <PinPad value={pin} onChange={setPin} />
                <Button onClick={verifyPin} disabled={pin.length !== 4 || verifying}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="verify-pin-btn">
                  {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Déverrouiller
                </Button>
              </>
            )}
            {isSuperAdmin && (
              <Button variant="outline" onClick={() => setShowSetPin(true)} className="w-full mt-3" data-testid="set-pin-btn">
                {pinConfigured ? 'Modifier le code PIN' : 'Configurer le code PIN'}
              </Button>
            )}
          </CardContent>
        </Card>
        <Dialog open={showSetPin} onOpenChange={setShowSetPin}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Code PIN d&apos;accès au module colis</DialogTitle>
              <DialogDescription>Ce code 4 chiffres sera partagé avec les agents autorisés à accéder au module.</DialogDescription>
            </DialogHeader>
            <PinPad value={newPin} onChange={setNewPin} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSetPin(false)}>Annuler</Button>
              <Button onClick={setModulePin} disabled={newPin.length !== 4} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-pin-btn">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---------- Main dashboard ----------
  return (
    <div className="space-y-6" data-testid="shipments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <Package className="w-7 h-7 text-amber-600" /> Module Colis
            <SyncBadge urlPattern="/shipments" />
          </h1>
          <p className="text-gray-500 text-sm">Envois, suivi, paiement et fidélisation client.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Actualiser</Button>
          <Button onClick={() => setScannerOpen(true)}
                  variant="outline"
                  className="border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38]/10"
                  data-testid="scan-qr-btn">
            <QrCode className="w-4 h-4 mr-2" /> Scanner un bordereau
          </Button>
          {/* Badge état réseau / file d'attente offline */}
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${
              !offlineScans.online
                ? 'bg-red-50 border-red-300 text-red-700'
                : offlineScans.pending > 0
                  ? 'bg-amber-50 border-amber-300 text-amber-800'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-700'
            }`}
            data-testid="offline-queue-badge"
            title={offlineScans.online
              ? (offlineScans.pending > 0 ? `${offlineScans.pending} scan(s) à synchroniser` : 'Connexion en ligne')
              : 'Hors ligne — les scans seront synchronisés au retour du réseau'}
          >
            {!offlineScans.online ? (
              <><WifiOff className="w-3.5 h-3.5" /> Hors ligne {offlineScans.pending > 0 ? `· ${offlineScans.pending}` : ''}</>
            ) : offlineScans.pending > 0 ? (
              <>
                <CloudUpload className={`w-3.5 h-3.5 ${offlineScans.syncing ? 'animate-pulse' : ''}`} />
                {offlineScans.pending} en attente
                <button
                  onClick={() => offlineScans.flush()}
                  disabled={offlineScans.syncing}
                  className="ml-1 underline hover:text-amber-900"
                  data-testid="offline-sync-btn"
                >
                  {offlineScans.syncing ? 'Sync…' : 'Sync'}
                </button>
              </>
            ) : (
              <><Wifi className="w-3.5 h-3.5" /> En ligne</>
            )}
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="new-shipment-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvel envoi
          </Button>
          {isSuperAdmin && (
            <Button variant="outline" onClick={() => setShowSetPin(true)}><KeyRound className="w-4 h-4 mr-1" /> Modifier PIN</Button>
          )}
          <Button variant="outline" onClick={relock}><Lock className="w-4 h-4 mr-1" /> Verrouiller</Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase font-bold text-gray-500">Total</div><div className="text-2xl font-bold text-[#1B3A4B]">{stats.total}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase font-bold text-gray-500">En transit</div><div className="text-2xl font-bold text-blue-700">{stats.in_transit}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase font-bold text-gray-500">Livrés</div><div className="text-2xl font-bold text-emerald-700">{stats.delivered}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase font-bold text-gray-500">En attente</div><div className="text-2xl font-bold text-amber-700">{stats.pending}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase font-bold text-gray-500">Revenus mois</div><div className="text-xl font-bold text-[#1B3A4B]">{fmt(stats.revenue_month)}</div></CardContent></Card>
          <Card><CardContent className="p-3"><div className="text-[10px] uppercase font-bold text-gray-500">À encaisser</div><div className="text-xl font-bold text-rose-700">{fmt(stats.revenue_pending)}</div></CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input className="pl-8 w-64" placeholder="N° suivi / nom / téléphone"
                 value={search} onChange={(e) => setSearch(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Loyalty leaderboard */}
      {loyalty.length > 0 && (
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3"><Award className="w-4 h-4 text-amber-600" /><h3 className="font-bold text-[#1B3A4B]">Top clients fidèles</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {loyalty.slice(0, 6).map(l => (
              <div key={l.phone} className="border rounded-lg p-2 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-[#1B3A4B]">{l.phone}</div>
                  <div className="text-[11px] text-gray-500">{l.total_shipments} envoi(s) · {fmt(l.total_spent)} FCFA</div>
                </div>
                <div className="text-right">
                  <Badge className={TIER_COLORS[l.tier] || TIER_COLORS.none}>{l.tier.toUpperCase()}</Badge>
                  <div className="text-[11px] text-amber-700 mt-0.5">{l.points} pts · {l.free_shipments_available} envoi(s) offert(s)</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* List */}
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">Aucun envoi enregistré.</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map(s => (
            <Card key={s.id} className="border-2 hover:border-amber-300 transition-colors" data-testid={`shipment-${s.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-xs font-mono text-amber-700 uppercase">{s.tracking_number}</div>
                    <div className="text-sm font-semibold text-[#1B3A4B] flex items-center gap-1">
                      <User className="w-3.5 h-3.5" /> {s.sender_name} → {s.recipient_name}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {s.origin_city} → {s.destination_city}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge className={`border ${STATUS_COLORS[s.status]}`}>{STATUS_LABELS[s.status]}</Badge>
                    {s.is_late && (
                      <Badge className="ml-1 bg-red-600 text-white border-red-700 animate-pulse" data-testid={`late-${s.id}`}>
                        En retard
                      </Badge>
                    )}
                    {s.delivery_included && (
                      <Badge className="ml-1 bg-emerald-600 text-white border-emerald-700" data-testid={`delivery-included-${s.id}`} title="Livraison incluse — aucun frais à l'arrivée à Bamako">
                        ★ Livraison incluse
                      </Badge>
                    )}
                    <div className="text-xs mt-1"><b>{fmt(s.amount)} FCFA</b> · {PAYMENT_STATUS_LABELS[s.payment_status]}</div>
                  </div>
                </div>
                {(s.estimated_departure_date || s.estimated_arrival_date) && (
                  <div className="flex items-center gap-3 text-[11px] text-gray-600 bg-[#F5EFE1]/40 border border-[#B85C38]/20 rounded-md px-2 py-1">
                    {s.estimated_departure_date && (
                      <span className="flex items-center gap-1"><Truck className="w-3 h-3 text-[#B85C38]" />
                        <b>Départ prévu :</b> {new Date(s.estimated_departure_date).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                    {s.estimated_arrival_date && (
                      <span className="flex items-center gap-1"><Package className="w-3 h-3 text-[#B85C38]" />
                        <b>Arrivée prévue :</b> {new Date(s.estimated_arrival_date).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                )}
                <div className="text-xs text-gray-600 line-clamp-2">{s.description}</div>
                {(s.packages && s.packages.length > 0) && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setExpandedPkgs(prev => ({ ...prev, [s.id]: !prev[s.id] }))}
                      className="text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded px-2 py-1 inline-flex items-center gap-1"
                      data-testid={`toggle-packages-${s.id}`}
                    >
                      <Package className="w-3 h-3" />
                      {s.packages.length} colis
                      {s.weight_kg ? ` · ${s.weight_kg} kg` : ''}
                      {expandedPkgs[s.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    {expandedPkgs[s.id] && (
                      <div className="mt-2 space-y-1.5 border border-amber-200 bg-amber-50/40 rounded-md p-2"
                           data-testid={`packages-list-${s.id}`}>
                        {s.packages.map((p, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2 bg-white rounded border px-2 py-1.5 flex-wrap"
                               data-testid={`pkg-item-${s.id}-${idx}`}>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-[#1B3A4B] truncate">
                                #{idx + 1} · {p.description}
                              </div>
                              <div className="text-[10px] text-gray-500 flex items-center gap-2 flex-wrap">
                                <span>{p.quantity || 1}× {p.weight_kg ? ` · ${p.weight_kg} kg` : ''}</span>
                                {(p.delivery_code || s.delivery_code) && (
                                  <span className="font-mono text-amber-700">
                                    Code : <b>{p.delivery_code || s.delivery_code}</b>
                                  </span>
                                )}
                                {p.delivered_at && (
                                  <span className="text-emerald-700">
                                    ✓ Livré le {p.delivered_at.slice(0, 10)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge className={`border text-[10px] ${STATUS_COLORS[p.status || 'pending']}`}>
                              {STATUS_LABELS[p.status || 'pending']}
                            </Badge>
                            <Select value={p.status || 'pending'} onValueChange={(v) => v && updatePackageStatus(s, idx, v)}>
                              <SelectTrigger className="h-7 text-xs w-32" data-testid={`pkg-status-${s.id}-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                              </SelectContent>
                            </Select>
                            {(p.status || 'pending') !== 'delivered' && (
                              <Button size="sm" variant="outline"
                                      onClick={() => { setPkgDelivery({ shipment: s, index: idx }); setPkgDeliveryCode(''); }}
                                      className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                      data-testid={`pkg-deliver-${s.id}-${idx}`}
                                      title={`Confirmer la remise du colis #${idx + 1}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Remettre
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="text-[10px] uppercase font-bold text-amber-700">Code remise : {s.delivery_code}</div>
                <div className="flex flex-wrap gap-1.5 pt-1 border-t">
                  <Select value="" onValueChange={(v) => v && updateStatus(s, v)}>
                    <SelectTrigger className="h-7 text-xs w-32"><SelectValue placeholder="Changer statut" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {s.payment_status !== 'paid' && (
                    <Button size="sm" variant="outline" onClick={() => markPaid(s)} className="h-7 text-xs">
                      <DollarSign className="w-3 h-3 mr-1" /> Marquer payé
                    </Button>
                  )}
                  {s.status !== 'delivered' && (
                    <Button size="sm" variant="outline" onClick={() => { setDeliveringId(s.id); setDeliveryCode(''); }} className="h-7 text-xs">
                      <Truck className="w-3 h-3 mr-1" /> Confirmer livraison
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => duplicateShipment(s)} className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`duplicate-${s.id}`} title="Créer un nouvel envoi avec ces mêmes expéditeur et destinataire">
                    <Copy className="w-3 h-3 mr-1" /> Dupliquer
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEtaEdit({
                      shipment_id: s.id,
                      tracking: s.tracking_number,
                      departure: (s.estimated_departure_date || '').slice(0, 10),
                      arrival: (s.estimated_arrival_date || '').slice(0, 10),
                    })}
                    className="h-7 text-xs border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38]/10"
                    data-testid={`eta-${s.id}`}
                    title="Modifier les dates prévisionnelles de départ et d'arrivée"
                  >
                    <Truck className="w-3 h-3 mr-1" /> Dates prévues
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => doPdf(s, 'bordereau', 'bordereau')} className="h-7 text-xs">
                    <Download className="w-3 h-3 mr-1" /> Bordereau
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareTracking(s)} className="h-7 text-xs"
                          data-testid={`share-${s.id}`}
                          title="Copie le lien de suivi + ouvre WhatsApp du destinataire">
                    <Share2 className="w-3 h-3 mr-1" /> Partager
                  </Button>
                  {(s.status === 'arrived_at_hub' || s.status === 'awaiting_pickup') && (
                    <Button
                      size="sm"
                      onClick={() => notifyArrivalAtHub(s)}
                      className="h-7 text-xs bg-[#B85C38] hover:bg-[#8F4426] text-white"
                      data-testid={`notify-arrival-${s.id}`}
                      title="Prévenir le destinataire par WhatsApp — colis prêt à être récupéré"
                    >
                      <MessageCircle className="w-3 h-3 mr-1" /> Prévenir WhatsApp
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => doPdf(s, 'receipt', 'recu')} className="h-7 text-xs">Reçu</Button>
                  {s.status === 'delivered' && (
                    <Button size="sm" variant="outline" onClick={() => doPdf(s, 'proof-of-delivery', 'preuve')} className="h-7 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Preuve
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) { setForm(emptyForm); setSenderLoyalty(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-modal">
          <DialogHeader><DialogTitle>Nouvel envoi</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div><Label>Type *</Label><Select value={form.shipment_type} onValueChange={(v) => setForm({ ...form, shipment_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="local">Local</SelectItem><SelectItem value="national">National</SelectItem><SelectItem value="international">International</SelectItem></SelectContent>
            </Select></div>
            <div><Label>Mode paiement</Label><Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="prepaid">Prépayé (MoMo/virement)</SelectItem><SelectItem value="cod">Payable à livraison (COD)</SelectItem><SelectItem value="on_pickup">Payé au dépôt</SelectItem></SelectContent>
            </Select></div>
            <div><Label>Expéditeur - Nom *</Label><Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} data-testid="sender-name" /></div>
            <div>
              <Label>Expéditeur - Téléphone *</Label>
              <Input value={form.sender_phone}
                     onChange={(e) => setForm({ ...form, sender_phone: e.target.value })}
                     onBlur={(e) => fetchLoyalty(e.target.value)}
                     data-testid="sender-phone" />
            </div>
            {senderLoyalty && senderLoyalty.total_shipments > 0 && (
              <div className="md:col-span-2 border-2 border-amber-300 bg-amber-50 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="sender-loyalty-block">
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-700" />
                  <div>
                    <div className="text-sm font-semibold text-[#1B3A4B]">Client fidèle reconnu</div>
                    <div className="text-xs text-gray-600">{senderLoyalty.total_shipments} envois · {fmt(senderLoyalty.total_spent)} FCFA cumulés</div>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={TIER_COLORS[senderLoyalty.tier] || TIER_COLORS.none}>{(senderLoyalty.tier || 'none').toUpperCase()}</Badge>
                  <div className="text-xs text-amber-700 mt-0.5 font-semibold">
                    {senderLoyalty.points} pts · {senderLoyalty.free_shipments_available} envoi(s) offert(s)
                  </div>
                </div>
              </div>
            )}
            <div><Label>Destinataire - Nom *</Label><Input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="recipient-name" /></div>
            <div><Label>Destinataire - Téléphone *</Label><Input value={form.recipient_phone} onChange={(e) => setForm({ ...form, recipient_phone: e.target.value })} /></div>
            <div><Label>Ville départ *</Label><Input value={form.origin_city} onChange={(e) => setForm({ ...form, origin_city: e.target.value })} /></div>
            <div><Label>Ville arrivée *</Label><Input value={form.destination_city} onChange={(e) => setForm({ ...form, destination_city: e.target.value })} data-testid="destination-city" /></div>

            <div className="md:col-span-2 border-t pt-3 mt-1">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold text-[#1B3A4B] flex items-center gap-1">
                  <Package className="w-4 h-4 text-amber-600" /> Colis ({form.packages?.length || 0})
                </Label>
                <Button type="button" size="sm" variant="outline" onClick={addPackage}
                        className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="add-package-btn">
                  <Plus className="w-3 h-3 mr-1" /> Ajouter un colis
                </Button>
              </div>
              <div className="space-y-2">
                {(form.packages || []).map((p, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-gray-50 border rounded-md p-2"
                       data-testid={`package-row-${idx}`}>
                    <div className="col-span-12 md:col-span-6">
                      <Label className="text-xs text-gray-600">Description colis {idx + 1} *</Label>
                      <Input value={p.description}
                             onChange={(e) => updatePackage(idx, 'description', e.target.value)}
                             placeholder="Ex: Documents, carton, valise…"
                             data-testid={`pkg-desc-${idx}`} />
                    </div>
                    <div className="col-span-5 md:col-span-2">
                      <Label className="text-xs text-gray-600">Poids (kg)</Label>
                      <Input type="number" step="0.1" value={p.weight_kg}
                             onChange={(e) => updatePackage(idx, 'weight_kg', e.target.value)}
                             data-testid={`pkg-weight-${idx}`} />
                    </div>
                    <div className="col-span-4 md:col-span-2">
                      <Label className="text-xs text-gray-600">Quantité</Label>
                      <Input type="number" min="1" value={p.quantity}
                             onChange={(e) => updatePackage(idx, 'quantity', e.target.value)}
                             data-testid={`pkg-qty-${idx}`} />
                    </div>
                    <div className="col-span-3 md:col-span-2 flex justify-end">
                      <Button type="button" size="sm" variant="ghost"
                              onClick={() => removePackage(idx)}
                              disabled={form.packages.length <= 1}
                              className="text-rose-600 hover:text-rose-800 hover:bg-rose-50 h-9"
                              data-testid={`remove-pkg-${idx}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-2"><Label>Montant total (FCFA) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="amount-input" /></div>

            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-emerald-400/50 bg-emerald-50/40 hover:bg-emerald-50 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!form.delivery_included}
                  onChange={(e) => setForm({ ...form, delivery_included: e.target.checked })}
                  className="w-4 h-4 accent-emerald-600"
                  data-testid="delivery-included-checkbox"
                />
                <span className="text-sm">
                  <b className="text-emerald-800">Livraison incluse</b>
                  <span className="text-emerald-700 ml-1">— Le montant couvre aussi la livraison à l&apos;arrivée à Bamako (aucun frais supplémentaire).</span>
                </span>
              </label>
            </div>

            <div>
              <Label className="flex items-center gap-1"><Truck className="w-3.5 h-3.5 text-[#B85C38]" /> Date prévisionnelle de départ</Label>
              <Input
                type="date"
                value={form.estimated_departure_date || ''}
                onChange={(e) => setForm({ ...form, estimated_departure_date: e.target.value })}
                data-testid="estimated-departure-date"
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><Package className="w-3.5 h-3.5 text-[#B85C38]" /> Date prévisionnelle d&apos;arrivée</Label>
              <Input
                type="date"
                value={form.estimated_arrival_date || ''}
                onChange={(e) => setForm({ ...form, estimated_arrival_date: e.target.value })}
                data-testid="estimated-arrival-date"
              />
              <p className="text-[11px] text-gray-500 mt-1">Si dépassée sans livraison, l&apos;envoi sera automatiquement marqué &ldquo;En retard&rdquo;.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={createShipment} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-shipment-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery confirmation modal */}
      <Dialog open={!!deliveringId} onOpenChange={(o) => !o && setDeliveringId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmation de livraison</DialogTitle>
            <DialogDescription>Le destinataire doit vous communiquer son code 4 chiffres reçu à l&apos;expédition.</DialogDescription>
          </DialogHeader>
          <PinPad value={deliveryCode} onChange={setDeliveryCode} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliveringId(null)}>Annuler</Button>
            <Button onClick={confirmDelivery} disabled={deliveryCode.length !== 4} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-delivery-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmer la remise
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ETA edit modal */}
      <Dialog open={!!etaEdit} onOpenChange={(o) => !o && setEtaEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2B3A2C]">
              <Truck className="w-5 h-5 text-[#B85C38]" /> Dates prévisionnelles
            </DialogTitle>
            <DialogDescription>
              {etaEdit && <>Envoi <b>{etaEdit.tracking}</b>. Ces dates servent au suivi et au calcul du retard.</>}
            </DialogDescription>
          </DialogHeader>
          {etaEdit && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="flex items-center gap-1 text-xs font-semibold"><Truck className="w-3.5 h-3.5 text-[#B85C38]" /> Date prévisionnelle de départ</Label>
                <Input
                  type="date"
                  value={etaEdit.departure || ''}
                  onChange={(e) => setEtaEdit({ ...etaEdit, departure: e.target.value })}
                  data-testid="eta-departure-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1 text-xs font-semibold"><Package className="w-3.5 h-3.5 text-[#B85C38]" /> Date prévisionnelle d&apos;arrivée</Label>
                <Input
                  type="date"
                  value={etaEdit.arrival || ''}
                  onChange={(e) => setEtaEdit({ ...etaEdit, arrival: e.target.value })}
                  data-testid="eta-arrival-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Si dépassée sans livraison, le colis sera automatiquement marqué <b>« En retard »</b>.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEtaEdit(null)}>Annuler</Button>
            <Button onClick={saveEtaDates} className="bg-[#B85C38] hover:bg-[#8F4426] text-white" data-testid="eta-save-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-package delivery modal */}
      <Dialog open={!!pkgDelivery} onOpenChange={(o) => !o && setPkgDelivery(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remise du colis #{pkgDelivery ? pkgDelivery.index + 1 : ''}</DialogTitle>
            <DialogDescription>
              {pkgDelivery && (
                <span>
                  <b>{pkgDelivery.shipment.tracking_number}</b> · {pkgDelivery.shipment.packages?.[pkgDelivery.index]?.description}
                  <br />
                  Saisissez le code 4 chiffres propre à ce colis (fourni au destinataire).
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <PinPad value={pkgDeliveryCode} onChange={setPkgDeliveryCode} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPkgDelivery(null)}>Annuler</Button>
            <Button onClick={confirmPackageDelivery} disabled={pkgDeliveryCode.length !== 4}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-pkg-delivery-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmer la remise du colis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set PIN modal (super-admin) */}
      <Dialog open={showSetPin} onOpenChange={setShowSetPin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le code PIN d&apos;accès</DialogTitle>
            <DialogDescription>Le nouveau code remplacera l&apos;ancien immédiatement.</DialogDescription>
          </DialogHeader>
          <PinPad value={newPin} onChange={setNewPin} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPin(false)}>Annuler</Button>
            <Button onClick={setModulePin} disabled={newPin.length !== 4} className="bg-amber-600 hover:bg-amber-700 text-white">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scanner modal */}
      <ShipmentQrScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onFound={handleScannedTracking}
        onDeliver={handleScanDeliver}
      />
    </div>
  );
};

export default Shipments;
